/**
 * @name Lazy Load BG
 * @author PLEJ
 */

'use strict';
const lazyBg = (() => {
    let images = [], iObserver, modernFormat;
    const selector = '[data-lazy-bg]';

    const config = {
        rootMargin: '300px 0px',
        threshold: 0.01
    };

    /**
     * Detect best supported modern image format (avif > webp > null).
     * Uses data URI image load — no UA sniffing.
     */
    const detectFormat = () => new Promise(resolve => {
        const avif = new Image();
        avif.onload = () => { modernFormat = 'avif'; resolve(); };
        avif.onerror = () => {
            const webp = new Image();
            webp.onload  = () => { modernFormat = 'webp'; resolve(); };
            webp.onerror = () => resolve();
            webp.src = 'data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA';
        };
        avif.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAABcAAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAAB9tZGF0EgAKCBgABogQEDQgAGQwR0AAIAAAAELBaA==';
    });

    /**
     * @function Init
     */
    const init = () => {
        detectFormat().then(() => {
            if ('IntersectionObserver' in window) {
                iObserver = new IntersectionObserver(handleIntersection, config);
            } else {
                window.addEventListener('scroll', () => {
                    images.forEach(image => checkSingle(image));
                });
            }

            document.querySelectorAll(selector).forEach(getElement);

            new (window.MutationObserver || window.WebKitMutationObserver)(
                (mutationList) => {
                    mutationList.forEach(
                        (mutation) => mutation.addedNodes.forEach((elem) => {
                            if (!elem || !elem.matches) return;

                            if (elem.matches(selector))
                                return getElement(elem);

                            elem.querySelectorAll(selector).forEach(getElement);
                        })
                    );
                }
            ).observe(document.body, { childList: true, subtree: true });
        });
    };

    /**
     * Get elements
     * @param {object} element
     */
    const getElement = (element) => {
        images.push(element);

        if ('IntersectionObserver' in window) {
            iObserver.observe(element);
        } else {
            checkSingle(element);
        }
    };

    /**
     * Load image — use modern format attribute when browser supports it.
     * @param {object} image
     */
    const loadImage = (image) => {
        const src = (modernFormat && image.dataset.lazyBgModern)
            ? image.dataset.lazyBgModern
            : image.dataset.lazyBg;

        if (!src) return;

        src.split(',').forEach(bg => setVariable(bg, image));

        image.removeAttribute('data-lazy-bg');
        image.removeAttribute('data-lazy-bg-modern');
        image.classList.add('lazy-ready');
    };

    /**
     * Set CSS custom property with bg image URL.
     * @param {string} string  "url|var-name"
     * @param {object} image
     */
    const setVariable = (string, image) => {
        const bg = string.split('|');
        image.style.setProperty('--' + bg[1], "url('" + bg[0].trim() + "')");
    };

    /**
     * Handle intersection
     * @param {array} entries
     */
    const handleIntersection = (entries) => {
        entries.forEach(entry => {
            if (entry.intersectionRatio > 0) {
                iObserver.unobserve(entry.target);
                loadImage(entry.target);
            }
        });
    };

    /**
     * @desc Check position single image
     */
    const _checkPosition = (image) => {
        return image.getBoundingClientRect().top * 0.3 <= window.innerHeight;
    };

    /**
     * Check single
     * @param {object} image
     */
    const checkSingle = (image) => {
        if (_checkPosition(image)) {
            loadImage(image);
        }
    };

    return {
        init: init
    };
})();

export {lazyBg}
